import { BarChartOutlined } from '@ant-design/icons';
import { Plus } from 'lucide-react';
import { useMemo } from 'react';

import { CenterFlex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import ScrollView from '@/components/Common/ScrollView';
import DataTable from '@/components/DataTable';
import Tabs from '@/components/Tab/Tabs';
import type { Tab } from '@/components/Tab/types';
import useTabs from '@/components/Tab/useTabs';
import Typo from '@/components/Typography/Typo';
import SettingHeader from '@/features/Setting/Header';
import useColor from '@/hooks/domain/useColor';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useUserStore from '@/stores/UserStore';

import { useChartListQuery } from '../hooks/use-chart-list-query';
import useChartListTable from '../hooks/use-chart-list-table';

const ChartList = () => {
  const color = useColor();
  const tableName = 'chart-list-table';
  const wr = useWorkspaceRouter();

  const { data } = useChartListQuery();

  const user = useUserStore((state) => state.user);

  const tabs: Tab[] = useMemo(() => {
    let myCount = 0;
    let starredCount = 0;
    data?.forEach((row) => {
      if (row.updated_by === user?.user_id) {
        myCount += 1;
      }
      if (row.starred) {
        starredCount += 1;
      }
    });
    return [
      {
        text: <Typo type="label2">All ({data?.length || 0})</Typo>,
        id: 'all',
      },
      {
        text: <Typo type="label2">My charts ({myCount})</Typo>,
        id: 'my',
      },
      {
        text: <Typo type="label2">Starred charts ({starredCount})</Typo>,
        id: 'starred',
      },
    ];
  }, [data, user?.user_id]);

  const [selectedTab, selectTab] = useTabs(tabs, 'tab');

  const onSelectTab = (tabId: StringNumberValue | null) => {
    selectTab(tabId);
  };

  const { tableData, columns } = useChartListTable(selectedTab);

  return (
    <>
      <SettingHeader
        title="Charts"
        rightNode={
          <Button
            onClick={() => {
              wr.push('/charts/create');
            }}
            sizeType="m"
            leftIcon={<Plus />}
          >
            Create chart
          </Button>
        }
      />
      <ScrollView style={{ height: `calc(100% - 56px)` }}>
        <VFlex padding="24px 36px" f={1}>
          <Tabs tabs={tabs} selectedTabId={selectedTab}>
            <Tabs.TabHeader
              tabColor="primary"
              tabSize="s"
              onSelect={onSelectTab}
            />
          </Tabs>
          <DataTable
            name={tableName}
            tableHeight="100%"
            datas={tableData}
            columns={columns}
            emptyDataPlaceholder={
              (data?.length || 0) === 0 ? (
                <VFlex g={36} a="center">
                  <CenterFlex
                    width="160px"
                    height="160px"
                    style={{
                      border: `4px solid ${color.primaryAlpha(0.3)}`,
                      borderRadius: 16,
                    }}
                  >
                    <BarChartOutlined
                      style={{ fontSize: 72, color: color.primaryAlpha(0.3) }}
                    />
                  </CenterFlex>
                  <Typo type="label1" color={color.gray500}>
                    Create a new chart or use a template to get started
                  </Typo>
                </VFlex>
              ) : (
                'No data'
              )
            }
            asyncData
          />
        </VFlex>
      </ScrollView>
    </>
  );
};

export default ChartList;
